import React from 'react';
import { action, observable, runInAction } from 'mobx';
import { Container, Inject } from 'typescript-ioc';
import { ICreateHubAttendeeDTO, IEventHubsDAO, IHubAttendeeDTO, IHubAttendeeListItemDTO, IHubOrganizationDTO } from '../../../../api/event-hubs.dao';
import { EventHubsDAO } from '../../../../api/implementations/event-hubs.dao';
import { IAPIErrorObject, IErrorParsing } from '../../../../infrastructure/error-parsing';
import { IAlertsService } from '../../../../infrastructure/alerts';
import { alertServerError, showNotification } from '../../../global/modal/alerts-host/alert-templates';
import Text from '../../../global/text/text';
import TrackingServiceProvider from '../../../../tracking-service/tracking-service-provider';
import { TRACKING_EVENTS } from '../../../../tracking-service/tracking-values';
import {
    DELETE_HUB_ATTENDEE_SUCCESS_TOAST_TEXT,
    EDIT_HUB_ATTENDEE_UPDATED_NOTIFICATION_TEXT,
    NEW_HUB_ATTENDEE_ADDED_NOTIFICATION_TEXT,
} from '../../event-hubs.text';
import { IAccountsDAO } from '../../../../api/accounts.dao';
import { AccountsDAO } from '../../../../api/implementations/accounts.dao';
import { HubAttendeeFormDataViewModel } from './hub-attendee-form-data.viewmodel';
import { ISubWindowsService } from '../../../../infrastructure/sub-windows';
import { HubOrganizationForm } from '../_hub-organization-form/hub-organization-form';

export class HubAttendeeFormVM {
    @observable
    public isSaving: boolean = false;
    @observable
    private attendeeFormDataVM: HubAttendeeFormDataViewModel;
    @observable
    private isLoaded: boolean = false;

    constructor(
        private readonly accountId: number,
        private readonly eventHubId: number,
        private readonly saveCompletedCallback: (attendee: IHubAttendeeDTO) => void,
        private readonly attendeeId?: number,
        private readonly initialAttendee?: IHubAttendeeListItemDTO,
        private readonly deleteHubAttendeeOnList?: () => void,
        @Inject private readonly eventHubsDAO: IEventHubsDAO = Container.get(EventHubsDAO),
        @Inject private readonly errorParsingService: IErrorParsing = Container.get(IErrorParsing),
        @Inject private readonly trackingService: TrackingServiceProvider = Container.get(TrackingServiceProvider),
        @Inject private readonly accountsDAO: IAccountsDAO = Container.get(AccountsDAO),
        @Inject private readonly subWindowsService: ISubWindowsService = Container.get(ISubWindowsService),
        @Inject private readonly alertsService: IAlertsService = Container.get(IAlertsService),
    ) {}

    public isEditMode(): boolean {
        return !!this.attendeeId;
    }

    public getAttendeeId(): number | undefined {
        return this.attendeeId;
    }

    public deleteAttendee = (): void => {
        const formData = this.attendeeFormDataVM;
        const firstName = formData?.firstName?.trim() ?? '';
        const lastName = formData?.lastName?.trim() ?? '';
        this.alertsService.confirm({
            message: (
                <>
                    <Text
                        className='alert-message'
                        id='oe.eventHubAttendees.deleteConfirmIntro'
                        description='Hub attendee delete confirmation'
                        message='Are you sure you want to delete this attendee? '
                    />
                    <Text
                        className='alert-message--danger'
                        id='oe.eventHubAttendees.deleteConfirmName'
                        description='Hub attendee delete confirmation name'
                        message={`${firstName} ${lastName}`.trim()}
                    />
                    <Text
                        className='alert-message'
                        id='oe.eventHubAttendees.deleteConfirmLoss'
                        description='Hub attendee delete confirmation consequence'
                        message=' will lose access to this Hub and all Events within it. This action cannot be undone.'
                    />
                </>
            ),
            confirmButtonProps: {
                text: 'Yes, Delete',
                modifier: 'confirm-warn',
                handler: this.deleteHubAttendeeApiCall,
            },
            cancelButtonProps: {
                modifier: 'cancel',
            },
        });
    };

    private deleteHubAttendeeApiCall = async (): Promise<void> => {
        if (!this.attendeeId) return;
        try {
            runInAction(() => {
                this.isSaving = true;
            });
            await this.eventHubsDAO.deleteHubAttendee(this.accountId, this.eventHubId, this.attendeeId);
            await this.trackDeleteHubAttendee();
            const firstName = this.attendeeFormDataVM.firstName?.trim() ?? '';
            const lastName = this.attendeeFormDataVM.lastName?.trim() ?? '';
            showNotification(
                DELETE_HUB_ATTENDEE_SUCCESS_TOAST_TEXT.defaultMessage
                    .replace('{firstName}', firstName)
                    .replace('{lastName}', lastName),
            );
            this.deleteHubAttendeeOnList?.();
        } catch (e) {
            const message = this.errorParsingService.extractMessage(e);
            alertServerError('Error', message || 'Unable to delete attendee');
        } finally {
            runInAction(() => {
                this.isSaving = false;
            });
        }
    };

    private async trackDeleteHubAttendee(): Promise<void> {
        try {
            const accountDetails = await this.accountsDAO.readAccount(this.accountId);
            this.trackingService.get().track(TRACKING_EVENTS.DELETE_HUB_ATTENDEE, {
                'Account Name': accountDetails.name,
                'Account ID': this.accountId,
                'Hub ID': this.eventHubId,
                'Attendee ID': this.attendeeId,
            });
        } catch {
            // tracking failure should not block the flow
        }
    }

    @action
    public async initialize(): Promise<void> {
        try {
            const organizations = await this.loadOrganizationsForAttendeeForm();
            runInAction(() => {
                this.attendeeFormDataVM = new HubAttendeeFormDataViewModel(organizations);
                if (this.initialAttendee) {
                    this.attendeeFormDataVM.populateFromListItem(this.initialAttendee);
                }
                this.isLoaded = true;
            });
        } catch {
            runInAction(() => {
                this.attendeeFormDataVM = new HubAttendeeFormDataViewModel([]);
                if (this.initialAttendee) {
                    this.attendeeFormDataVM.populateFromListItem(this.initialAttendee);
                }
                this.isLoaded = true;
            });
        }
    }

    public getAttendeeFormDataVM(): HubAttendeeFormDataViewModel {
        return this.attendeeFormDataVM;
    }

    public getIsLoaded(): boolean {
        return this.isLoaded;
    }

    public formSubmitAction = async (): Promise<boolean> => {
        if (!this.attendeeFormDataVM.checkForm()) {
            return false;
        }
        return this.attendeeId
            ? await this.updateAttendee()
            : await this.createAttendee();
    };

    public openNewOrganizationPopup = (): void => {
        this.subWindowsService.showSubWindow({
            title: 'New Organization',
            componentToHost: HubOrganizationForm,
            componentToHostProps: {
                accountId: this.accountId,
                eventHubId: this.eventHubId,
                updateOrganizationOnList: (organization?: IHubOrganizationDTO) => {
                    if (organization) {
                        const organizations = this.attendeeFormDataVM.getOrganizations();
                        const alreadyExists = organizations.some(item => item.hubOrganizationId === organization.hubOrganizationId);
                        if (!alreadyExists) {
                            this.attendeeFormDataVM.updateOrganizationsList([...organizations, organization]);
                        }
                        this.attendeeFormDataVM.setOrganizationId(organization.hubOrganizationId);
                        return;
                    }
                    void this.refreshOrganizations();
                },
            },
        });
    };

    @action
    public async refreshOrganizations(): Promise<void> {
        try {
            const organizations = await this.loadOrganizationsForAttendeeForm();
            runInAction(() => {
                this.attendeeFormDataVM.updateOrganizationsList(organizations);
            });
        } catch {
            // silently fail — organizations list stays as-is
        }
    }

    private async loadOrganizationsForAttendeeForm(): Promise<IHubOrganizationDTO[]> {
        const organizations = await this.eventHubsDAO.getHubOrganizations(this.accountId, this.eventHubId);
        return organizations.slice().sort((a, b) => (a.name || '').localeCompare(b.name || ''));
    }

    @action
    private async createAttendee(): Promise<boolean> {
        this.isSaving = true;
        try {
            const dto: ICreateHubAttendeeDTO = this.attendeeFormDataVM.toCreateDTO();
            const response = await this.eventHubsDAO.createHubAttendee(this.accountId, this.eventHubId, dto);

            showNotification(NEW_HUB_ATTENDEE_ADDED_NOTIFICATION_TEXT.defaultMessage);
            this.trackCreateHubAttendee();
            this.saveCompletedCallback?.(response);
            return true;
        } catch (e) {
            const apiError = this.errorParsingService.extractAPIError(e);
            this.handleApiException(apiError);
            return false;
        } finally {
            runInAction(() => {
                this.isSaving = false;
            });
        }
    }

    @action
    private async updateAttendee(): Promise<boolean> {
        if (!this.attendeeId) return false;
        this.isSaving = true;
        try {
            const dto: ICreateHubAttendeeDTO = this.attendeeFormDataVM.toCreateDTO();
            const response = await this.eventHubsDAO.updateHubAttendee(
                this.accountId,
                this.eventHubId,
                this.attendeeId,
                dto,
            );

            showNotification(EDIT_HUB_ATTENDEE_UPDATED_NOTIFICATION_TEXT.defaultMessage);
            this.trackEditHubAttendee(dto.registrationStatus);
            this.saveCompletedCallback?.(response);
            return true;
        } catch (e) {
            const apiError = this.errorParsingService.extractAPIError(e);
            this.handleApiException(apiError);
            return false;
        } finally {
            runInAction(() => {
                this.isSaving = false;
            });
        }
    }

    private async trackEditHubAttendee(newRegistrationStatus: string): Promise<void> {
        try {
            const accountDetails = await this.accountsDAO.readAccount(this.accountId);
            const existingStatus = this.initialAttendee?.registrationStatus ?? '';
            const changed = existingStatus !== newRegistrationStatus;
            const registrationStatusValue = changed
                ? `Changed - ${newRegistrationStatus}`
                : `Unchanged - ${existingStatus}`;

            this.trackingService.get().track(TRACKING_EVENTS.EDIT_HUB_ATTENDEE, {
                'Account Name': accountDetails.name,
                'Account ID': this.accountId,
                'Hub ID': this.eventHubId,
                'Attendee ID': this.attendeeId,
                'Registration Status': registrationStatusValue,
            });
        } catch {
            // tracking failure should not block the flow
        }
    }

    @action
    private handleApiException(apiError: IAPIErrorObject): void {
        const apiToFormFieldMap: { [key: string]: string } = {
            EMAIL_NO_UNIQUE: 'email',
            EXTERNAL_ATTENDEE_ID_NO_UNIQUE: 'externalAttendeeId',
        };
        const apiFieldNames = Object.keys(apiToFormFieldMap);

        if (apiFieldNames.length && apiError?.fields?.length) {
            const fieldsMatch = apiError.fields.reduce((match, { name, message }) => {
                if (apiFieldNames.includes(name)) match[name] = message;
                return match;
            }, {} as { [key: string]: string });

            if (Object.keys(fieldsMatch).length) {
                this.attendeeFormDataVM.setServerErrorMessage('');
                Object.entries(fieldsMatch).forEach(([apiField, message]) => {
                    const formField = apiToFormFieldMap[apiField];
                    this.attendeeFormDataVM.setFieldError(formField, message);
                });
                return;
            }
        }

        this.attendeeFormDataVM.clearAllFieldErrors();
        this.attendeeFormDataVM.setServerErrorMessage(apiError?.errorMessage || 'An error occurred');
    }

    private async trackCreateHubAttendee(): Promise<void> {
        try {
            const accountDetails = await this.accountsDAO.readAccount(this.accountId);
            this.trackingService.get().track(TRACKING_EVENTS.CREATE_HUB_ATTENDEE, {
                'Account Name': accountDetails.name,
                'Account ID': this.accountId,
                'Hub ID': this.eventHubId,
            });
        } catch {
            // tracking failure should not block the flow
        }
    }
}
